// quizSlice.js
import { createSlice } from "@reduxjs/toolkit";
import {
  createQuiz,
  getLeaderboardDetails,
  getQuizzes,
  getRegisteredQuizUsersList,
  getStudentResults,
} from "./thunk";
import { fetchThemes } from "./thunk";
import { fetchQuizTypes } from "./thunk";
import { fetchSubjects } from "./thunk";
import { fetchSubjectBasedClassesData } from "./thunk";
import { Leaderboard } from "@mui/icons-material";

const initialState = {
  quizForm: {
    title: "",
    start: "",
    registrationStart: "",
    registrationEnd: "",
    totalQuestions: "",
    questionDuration: "",
    correct: "1",
    wrong: "0",
    skipped: "0",
    subject_id: "",
    class_id: "",
    enableWrongMarks: false,
    enableSkippedMarks: false,
    registrationFeeMode: "",
    registrationCost: 1,
    topWinnersCount: 0,
    winnerPrizes: [],
    about: "",
    description: "",
    terms: "",
    prizeDescription: "",
  },
  quizType: "",
  updateQuizForm: {
    id: "",
    title: "",
    start: "",
    registrationStart: "",
    registrationEnd: "",
    totalQuestions: "",
    questionDuration: "",
    correct: "1",
    wrong: "0",
    skipped: "0",
    enableWrongMarks: false,
    enableSkippedMarks: false,
    registrationFeeMode: "",
    registrationCost: 0,
    topWinnersCount: 0,
    winnerPrizes: [],
    about_quiz: "",
    quiz_terms: "",
    updated_by: "",
    quizType: "",
    class_id: "",
    themeData: [],
    themeId: "",
    subject_id: "",
    law_selected: "",
    description: "",
    group_idd: [],
    class_ids: [],
    subjectSpecificClasses: [],
  },
  offset: "",
  // for the quiz status
  subThemes: [],
  selectedSubThemes: [],
  quizzes: [],
  selectedQuiz: null,
  showPopup: false,
  option: "",
  loading: false,
  error: null,
  // for user mgt
  option_selected: "",
  selectedQuizId: "",
  subFilterBy: "",
  limit: "",
  practiceList: [], // for the practice quiz
  subjectBasedClasses: [],

  // from school quiz slice
  themes: [],
  themesStatus: "idle",
  themesError: null,
  quizTypesList: [],
  subjectsList: [],
  liveParticipants: [],
  resultInfo: [],
  leaderboardInfo: [],
};

const createAsyncCases = (builder, asyncThunk, key, stateKey = null) => {
  builder
    .addCase(asyncThunk.pending, (state) => {
      state[`${key}Status`] = "loading";
      state[`${key}Error`] = null; // reset error on new request
    })
    .addCase(asyncThunk.fulfilled, (state, action) => {
      state[`${key}Status`] = "succeeded";
      if (stateKey) state[stateKey] = action.payload;
    })
    .addCase(asyncThunk.rejected, (state, action) => {
      state[`${key}Status`] = "failed";
      state[`${key}Error`] = action.error?.message || "Something went wrong";
    });
};

const quizSlice = createSlice({
  name: "quiz",
  initialState,
  reducers: {
    setQuizForm: (state, action) => {
      const { key, value } = action.payload;
      state.quizForm[key] = value;
    },
    // resetQuizForm: (state) => {
    //   state.quizForm = initialState.quizForm;
    // },
    resetQuizForm: (state) => {
      state.quizForm = {
        ...state.quizForm,

        title: "",
        start: "",
        registrationStart: "",
        registrationEnd: "",
        totalQuestions: "",
        questionDuration: "",
        correct: "1",
        wrong: "0",
        skipped: "0",
        subject_id: "",
        class_id: "",
        enableWrongMarks: false,
        enableSkippedMarks: false,
        registrationFeeMode: "",
        registrationCost: 1,
        topWinnersCount: 0,
        winnerPrizes: [],
        description: "",
        prizeDescription: "",
      };
    },
    updateWinnerPrize: (state, action) => {
      const { index, value } = action.payload;
      state.quizForm.winnerPrizes[index] = value;
    },

    // for the updation
    setUpdateQuizForm: (state, action) => {
      if (action.payload.key) {
        // single key update
        const { key, value } = action.payload;
        state.updateQuizForm[key] = value;
      } else {
        // multiple keys update
        Object.entries(action.payload).forEach(([key, value]) => {
          state.updateQuizForm[key] = value;
        });
      }
    },

    resetUpdateQuizForm: (state) => {
      state.quizForm = initialState.quizForm;
    },
    updateWinnerPrizeNew: (state, action) => {
      const { index, value } = action.payload;
      state.updateQuizForm.winnerPrizes[index] = value;
    },
    setState: (state, action) => {
      const { key, value } = action.payload;
      state[key] = value;
    },
    clearSubjectBasedClasses: (state, action) => {
      state.subjectBasedClasses = [];
    },
  },
  extraReducers: (builder) => {
    createAsyncCases(builder, fetchThemes, "themes", "themes");
    createAsyncCases(builder, fetchQuizTypes, "quizTypesList", "quizTypesList");
    createAsyncCases(builder, fetchSubjects, "subjectsList", "subjectsList");
    createAsyncCases(
      builder,
      fetchSubjectBasedClassesData,
      "subjectBasedClasses",
      "subjectBasedClasses",
    );

    builder
      .addCase(createQuiz.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createQuiz.fulfilled, (state) => {
        state.loading = false;
        state.quizForm = initialState.quizForm;
      })
      .addCase(createQuiz.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to create quiz";
      })

      .addCase(getQuizzes.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getQuizzes.fulfilled, (state, action) => {
        state.loading = false;
        state.quizzes = action.payload; // ✅ FIXED
      })
      .addCase(getQuizzes.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch users";
      })
      .addCase(getRegisteredQuizUsersList.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getRegisteredQuizUsersList.fulfilled, (state, action) => {
        state.loading = false;
        state.liveParticipants = action.payload; // ✅ FIXED
      })
      .addCase(getRegisteredQuizUsersList.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch users";
      })
      .addCase(getStudentResults.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getStudentResults.fulfilled, (state, action) => {
        state.loading = false;
        state.resultInfo = action.payload; // ✅ FIXED
      })
      .addCase(getStudentResults.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch users";
      })
      .addCase(getLeaderboardDetails.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getLeaderboardDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.leaderboardInfo = action.payload; // ✅ FIXED
      })
      .addCase(getLeaderboardDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch users";
      });
  },
});

export const {
  setQuizForm,
  resetQuizForm,
  updateWinnerPrize,
  addAllotment,
  updateAllotmentPercent,
  setState,
  updateWinnerPrizeNew,
  setUpdateQuizForm,
  resetUpdateQuizForm,
  clearSubjectBasedClasses,
} = quizSlice.actions;

export default quizSlice.reducer;
